import { Flex } from 'antd';
import { TabItem } from '@/components/Tab/TabBar';
import { In_TRANSIT, LOCAL_AR, STYLE_ORDERS } from '@/config';
import { OrderStatusTab } from '@/types/columns.ts';
import { listStatusButtons, shortenNumber } from '@/utils';
import FilterButton from '@/components/FilterButton/FilterButton.tsx';
import { OrderStatusCountResponse } from '@/types';

interface TabItemsProps {
  t: (key: string) => string;
  i18n: {
    language: string;
  };
  checkActive: (item: string) => 'primary' | 'default';
  handleChangeSubTab: (activeSubTab: string) => void;
  onHoldItemsCount?: number;
  orderCount?: OrderStatusCountResponse;
  /** Total count from the inventory API, shown as a badge on the Inventory tab. */
  inventoryTotal?: number;
  /** Whether the delay RTO & resend feature is enabled for this merchant. */
  isDelayRtoEnabled?: boolean;
}

interface TabConfig {
  key: OrderStatusTab;
  titleKey: string;
  hasContent?: boolean;
  badgeKey?: string;
  dashboardCardColSize: number;
}

//finished
//in_progress
//rto

export const TAB_CONFIGS: TabConfig[] = [
  {
    key: OrderStatusTab.READY_TO_SHIP,
    titleKey: 'ready',
    badgeKey: 'ready_to_ship',
    dashboardCardColSize: 12,
  },
  {
    key: OrderStatusTab.IN_PROGRESS,
    titleKey: 'in_progress',
    hasContent: true,
    badgeKey: 'in_progress',
    dashboardCardColSize: 4,
  },
  {
    key: OrderStatusTab.ON_HOLD,
    titleKey: 'on_hold',
    hasContent: true,
    badgeKey: 'on_hold',
    dashboardCardColSize: 12,
  },
  {
    key: OrderStatusTab.RTO,
    titleKey: 'return_to_origin',
    hasContent: true,
    badgeKey: 'return_to_origin',
    dashboardCardColSize: 5,
  },
  {
    key: OrderStatusTab.INVENTORY,
    titleKey: 'inventory',
    badgeKey: 'inventory',
    dashboardCardColSize: 4,
  },
  {
    key: OrderStatusTab.FINISHED,
    titleKey: 'finished',
    hasContent: true,
    badgeKey: 'finished',
    dashboardCardColSize: 4,
  },
];

type CountableTab = Exclude<OrderStatusTab, typeof OrderStatusTab.INVENTORY>;

type TabProps<T extends CountableTab> = {
  t: (key: string) => string;
  i18n: { language: string };
  checkActive: (item: string) => 'primary' | 'default';
  handleChangeSubTab: (activeSubTab: string) => void;
  tabKey: T;
  subTabOrderCounts?: OrderStatusCountResponse[T];
};

const TabContent = <T extends CountableTab>({
  t,
  i18n,
  checkActive,
  handleChangeSubTab,
  tabKey,
  subTabOrderCounts,
}: TabProps<T>) => (
  <Flex className={STYLE_ORDERS}>
    <button
      type='button'
      onClick={() => handleChangeSubTab('all')}
      className={[
        'flex items-center gap-1.5 h-9 px-4 rounded-lg text-[13px] font-semibold cursor-pointer transition-colors border border-solid',
        checkActive('all') === 'primary'
          ? 'bg-[rgb(237,224,74)] border-[rgb(237,224,74)] text-[rgb(57,57,60)]'
          : 'bg-white border-[rgb(232,230,227)] text-[rgb(57,57,60)] hover:bg-[rgb(245,245,243)]',
        i18n.language === LOCAL_AR ? In_TRANSIT : '',
      ].join(' ')}
    >
      {t('all')}
    </button>
    {listStatusButtons[tabKey]?.map((item) => {
      const key = item.badgeKey as keyof OrderStatusCountResponse[T];
      const count: number | undefined = subTabOrderCounts?.[key] as number;

      return (
        <FilterButton
          key={item.value}
          item={{ ...item }}
          setActiveButton={handleChangeSubTab}
          checkActive={checkActive}
          badgeCount={count}
        />
      );
    })}
  </Flex>
);

/**
 * Generates tab items for the order status tabs
 * @param props - The props for generating tab items
 * @returns An array of TabItem objects
 */
export const getTabItems = ({
  t,
  i18n,
  checkActive,
  handleChangeSubTab,
  orderCount,
  inventoryTotal,
  isDelayRtoEnabled,
}: TabItemsProps): TabItem[] =>
  TAB_CONFIGS.filter(({ key }) => {
    if (key === OrderStatusTab.INVENTORY && !isDelayRtoEnabled) return false;
    return true;
  }).map(({ key, titleKey, hasContent, badgeKey }) => {
    // For inventory tab, prefer badge count API, fall back to inventory API total.
    const isInventoryTab = key === OrderStatusTab.INVENTORY;

    const orderBadgeCount = badgeKey && orderCount
      ? orderCount[badgeKey as keyof OrderStatusCountResponse]?.total
      : isInventoryTab
        ? inventoryTotal
        : undefined;

    return {
      key,
      title: t(titleKey),
      ...(hasContent && {
        content: (
          <TabContent
            t={t}
            i18n={i18n}
            checkActive={checkActive}
            handleChangeSubTab={handleChangeSubTab}
            tabKey={key as CountableTab}
            subTabOrderCounts={orderCount?.[badgeKey as keyof OrderStatusCountResponse] as OrderStatusCountResponse[CountableTab] | undefined}
          />
        ),
      }),
      ...(orderBadgeCount !== undefined
        ? { badge: orderBadgeCount }
        : badgeKey && orderCount
          ? { badge: orderBadgeCount }
          : {}),
    };
  });
